import type {
  ScheduledFunction,
  CreateScheduledFunctionRequest,
  UpdateScheduledFunctionRequest,
  ScheduledFunctionListResponse,
  RunScheduledFunctionResponse,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export async function getScheduledFunctions(signal?: AbortSignal): Promise<ScheduledFunction[]> {
  const response = await fetch(`${API_BASE_URL}/scheduled-functions`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch scheduled functions: ${response.status} ${response.statusText}`);
  }

  const data: ScheduledFunctionListResponse = await response.json();
  return data.items;
}

export async function createScheduledFunction(
  payload: CreateScheduledFunctionRequest,
  signal?: AbortSignal
): Promise<ScheduledFunction> {
  const response = await fetch(`${API_BASE_URL}/scheduled-functions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to create scheduled function: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }

  return response.json();
}

export async function updateScheduledFunction(
  id: string,
  payload: UpdateScheduledFunctionRequest,
  signal?: AbortSignal
): Promise<ScheduledFunction> {
  const response = await fetch(`${API_BASE_URL}/scheduled-functions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to update scheduled function: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }

  const data = await response.json();
  return data.item ?? data;
}

export async function deleteScheduledFunction(
  id: string,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/scheduled-functions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to delete scheduled function: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }
}

export async function runScheduledFunction(
  id: string,
  signal?: AbortSignal
): Promise<RunScheduledFunctionResponse> {
  const response = await fetch(`${API_BASE_URL}/scheduled-functions/${encodeURIComponent(id)}/run`, {
    method: 'POST',
    headers: { 'Accept': 'application/json' },
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to run scheduled function: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }

  return response.json();
}
